import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";

/**
 * Public legal page. No auth required. Content is tailored to the
 * Nationwide Medical Insurance Provider Network Manager — an internal
 * ops tool with a bound-user roster (no public sign-up).
 */
export default function Terms() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="terms-page">
      <LegalHeader />

      <main className="mx-auto max-w-3xl px-6 py-10 pb-20">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-nmi-700">
          Legal · Effective Feb 18, 2026
        </div>
        <h1 className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
          Terms of Service
        </h1>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
          These Terms govern your use of the <b>Nationwide Medical Insurance
          Provider Network Manager</b> (the "Service") — a private, internal
          platform operated by Nationwide Medical Insurance Ghana Ltd. ("NMI",
          "we", "us") for managing the Healthcare Service Provider (HSP) network
          and its Telegram bot companion.
        </p>

        <Section n="1" title="Who these Terms apply to">
          The Service is a closed, invite-only tool. Access is granted to
          named NMI staff, approved auditors, and specifically-provisioned
          Telegram users. By signing in on the web or interacting with the
          NMI Telegram bot with a pre-provisioned account, you agree to
          these Terms and to the <Link to="/privacy" className={link}>Privacy Policy</Link>.
          If you do not agree, do not use the Service.
        </Section>

        <Section n="2" title="Your account">
          <ul className={ul}>
            <li>Your account is personal. Do not share your password, cookies, or Telegram binding with anyone.</li>
            <li>Notify NMI immediately if you suspect your credentials are compromised. We may force a password reset without notice to protect the network.</li>
            <li>You must change your temporary password on first sign-in.</li>
            <li>NMI may suspend, revoke, or delete any account for policy violations, role changes, or security reasons.</li>
          </ul>
        </Section>

        <Section n="3" title="Acceptable use">
          You agree not to:
          <ul className={ul}>
            <li>Access, edit, or export data you are not authorised to see.</li>
            <li>Circumvent role-based permissions, throttles, or the Telegram
              command allow-list.</li>
            <li>Scrape, resell, or redistribute facility data, company data,
              or member-contact information outside NMI channels.</li>
            <li>Attempt to intercept traffic, brute-force the sign-in flow, or
              probe for vulnerabilities without written authorisation.</li>
            <li>Impersonate another staff member, auditor, or provider.</li>
          </ul>
        </Section>

        <Section n="4" title="Data you enter or upload">
          You are responsible for the accuracy of any facility record,
          company plan, contact detail, or note you enter or import. NMI
          retains full audit logs of every create, edit, suspend, reactivate,
          terminate, import, and role change — indefinitely — for compliance,
          dispute resolution, and forensic review.
        </Section>

        <Section n="5" title="Telegram bot">
          The Service exposes a Telegram bot for search, suspend/reactivate,
          watchlist, and reporting. When you interact with the bot using a
          bound account, your Telegram user ID and username are logged
          against the corresponding action. Messages you send to the bot
          are processed by NMI's servers and may be stored in audit logs.
          The bot enforces the same role-based permissions as the web app.
        </Section>

        <Section n="6" title="Confidentiality">
          Facility contacts, member data, plan structures, and rate cards
          you access via the Service are <b>NMI confidential information</b>.
          You must not disclose them outside authorised channels, including
          after your account is deprovisioned. Confidentiality obligations
          survive termination of these Terms.
        </Section>

        <Section n="7" title="Availability, changes, and beta features">
          We aim for high availability but do not guarantee uninterrupted
          Service. Scheduled maintenance and emergency changes may occur
          without notice. Features marked "beta" (e.g. AI-assisted bulk
          edit) may change or be withdrawn.
        </Section>

        <Section n="8" title="Third-party services">
          The Service uses Telegram (messaging), Resend (email delivery),
          MongoDB Atlas (data storage), Emergent LLM (AI assist), and
          Sentry (error monitoring). Their handling of the limited data
          they process is subject to their own terms.
        </Section>

        <Section n="9" title="Termination">
          We may terminate or suspend your access at any time — for role
          change, non-use, misconduct, or security reasons. On termination,
          your web login and Telegram binding are revoked immediately.
          Audit records tied to your prior activity are retained.
        </Section>

        <Section n="10" title="Liability">
          The Service is provided "as is" for internal ops use. To the
          maximum extent permitted by law, NMI is not liable for indirect,
          incidental, or consequential loss arising from Service use or
          downtime. Nothing in these Terms limits liability that cannot
          be excluded under Ghanaian law.
        </Section>

        <Section n="11" title="Governing law">
          These Terms are governed by the laws of the Republic of Ghana.
          Any dispute is subject to the exclusive jurisdiction of the
          courts of Ghana.
        </Section>

        <Section n="12" title="Changes to these Terms">
          We may update these Terms at any time. Material changes will
          be communicated via email to the address on your account or via
          an in-app notice. Continued use after an update constitutes
          acceptance.
        </Section>

        <Section n="13" title="Contact">
          Questions or concerns about these Terms?
          <div className="mt-2 rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm">
            <div><b>Nationwide Medical Insurance Ghana Ltd.</b></div>
            <div>Provider Network Team</div>
            <div>Email: <a className={link} href="mailto:pnm@nationwidemh.com">pnm@nationwidemh.com</a></div>
          </div>
        </Section>

        <div className="mt-10 rounded-md border border-slate-200 bg-white p-4 text-xs text-slate-500">
          Read our <Link to="/privacy" className={link}>Privacy Policy</Link> for
          details on how we handle your personal information under the
          <b> Data Protection Act, 2012 (Act 843)</b>.
        </div>
      </main>
    </div>
  );
}

const link = "font-semibold text-nmi-700 underline underline-offset-2 hover:text-nmi-800";
const ul = "mt-2 list-disc pl-5 space-y-1.5 text-sm text-slate-700 leading-relaxed";

const Section = ({ n, title, children }) => (
  <section className="mt-8 border-t border-slate-200 pt-6">
    <h2 className="font-heading text-lg font-bold text-slate-900">
      <span className="mr-2 text-slate-400">{n}.</span>{title}
    </h2>
    <div className="mt-2 text-sm text-slate-700 leading-relaxed">{children}</div>
  </section>
);

export const LegalHeader = () => (
  <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/85 backdrop-blur">
    <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-3.5">
      <Link to="/" className="flex items-center gap-2 text-sm font-bold text-slate-900 hover:text-nmi-700" data-testid="legal-home-link">
        <ShieldCheck size={16} className="text-nmi-700" strokeWidth={2.5} />
        Nationwide MI · Provider Network
      </Link>
      <Link to="/login" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-nmi-700">
        <ArrowLeft size={12} /> Back to sign-in
      </Link>
    </div>
  </header>
);
